import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  ArrowRight, 
  User, 
  Heart, 
  Briefcase, 
  Users, 
  MapPin, 
  Utensils,
  Target,
  Camera,
  CheckCircle,
  Loader2,
  AlertCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/vivah`;

const STEPS = [
  { id: 1, title: 'Basic Info', icon: User },
  { id: 2, title: 'Religious', icon: Heart },
  { id: 3, title: 'Education & Career', icon: Briefcase },
  { id: 4, title: 'Family', icon: Users },
  { id: 5, title: 'Location', icon: MapPin },
  { id: 6, title: 'Lifestyle', icon: Utensils },
  { id: 7, title: 'Partner Preferences', icon: Target },
  { id: 8, title: 'Photos', icon: Camera }
];

export default function VivahRegisterPage({ user }) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [constants, setConstants] = useState(null);
  const [formData, setFormData] = useState({
    // Basic Info
    profile_for: 'Self',
    full_name: user?.name || '',
    gender: '',
    dob: '',
    height_cm: 165,
    weight_kg: '',
    complexion: '',
    body_type: '',
    blood_group: '',
    marital_status: 'Never Married',
    has_children: false,
    number_of_children: 0,
    physical_status: 'Normal',
    disability_details: '',
    
    // Religious
    religion: 'Hindu',
    caste: '',
    sub_caste: '',
    gotra: '',
    rashi: '',
    nakshatra: '',
    manglik: "Don't Know",
    horoscope_match_required: false,
    
    // Education & Career
    highest_education: '',
    education_detail: '',
    profession: '',
    profession_detail: '',
    annual_income: 'Not Specified',
    working_city: '',
    working_country: 'India',
    
    // Family
    father_name: '',
    father_occupation: '',
    mother_name: '',
    mother_occupation: '',
    family_type: '',
    family_values: '',
    family_status: '',
    brothers: 0,
    brothers_married: 0,
    sisters: 0,
    sisters_married: 0,
    family_location: '',
    native_place: '',
    about_family: '',
    
    // Contact
    city: '',
    state: '',
    country: 'India',
    citizenship: 'Indian',
    residency_status: 'Citizen',
    
    // Lifestyle
    diet: 'Vegetarian',
    smoking: 'No',
    drinking: 'No',
    hobbies: [],
    
    // Partner Preferences
    partner_age_min: 21,
    partner_age_max: 35,
    partner_height_min_cm: 150,
    partner_height_max_cm: 190,
    partner_marital_status: ['Never Married'],
    partner_religion: [],
    partner_caste: [],
    partner_education: [],
    partner_profession: [],
    partner_income_min: '',
    partner_country: ['India'],
    partner_state: [],
    partner_manglik: "Doesn't Matter",
    about_partner: '',
    
    // About Me
    about_me: '',
    
    // Photos
    photos: [],
    profile_photo: ''
  });

  useEffect(() => {
    fetchConstants();
  }, []);

  const fetchConstants = async () => {
    try {
      const response = await axios.get(`${API}/constants`);
      setConstants(response.data);
    } catch (error) {
      console.error('Failed to fetch constants:', error);
      toast.error('Failed to load form data');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleArrayChange = (field, value) => {
    setFormData(prev => {
      const arr = prev[field] || [];
      if (arr.includes(value)) {
        return { ...prev, [field]: arr.filter(v => v !== value) };
      } else {
        return { ...prev, [field]: [...arr, value] };
      }
    });
  };

  const nextStep = () => {
    if (validateStep()) {
      setCurrentStep(prev => Math.min(prev + 1, STEPS.length));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const validateStep = () => {
    switch (currentStep) {
      case 1:
        if (!formData.full_name || !formData.gender || !formData.dob) {
          toast.error('Please fill all required fields');
          return false;
        }
        break;
      case 2:
        if (!formData.religion) {
          toast.error('Please select your religion');
          return false;
        }
        break;
      case 3:
        if (!formData.highest_education || !formData.profession) {
          toast.error('Please fill education and profession');
          return false;
        }
        break;
      case 5:
        if (!formData.city || !formData.state) {
          toast.error('Please fill city and state');
          return false;
        }
        break;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('dharmbook_token');
      
      const payload = {
        basic: {
          profile_for: formData.profile_for,
          full_name: formData.full_name,
          gender: formData.gender,
          dob: formData.dob,
          height_cm: parseInt(formData.height_cm),
          weight_kg: formData.weight_kg ? parseInt(formData.weight_kg) : null,
          complexion: formData.complexion || null,
          body_type: formData.body_type || null,
          blood_group: formData.blood_group || null,
          marital_status: formData.marital_status,
          has_children: formData.has_children,
          number_of_children: formData.number_of_children || null,
          physical_status: formData.physical_status,
          disability_details: formData.disability_details || null
        },
        religious: {
          religion: formData.religion,
          caste: formData.caste || null,
          sub_caste: formData.sub_caste || null,
          gotra: formData.gotra || null,
          rashi: formData.rashi || null,
          nakshatra: formData.nakshatra || null,
          manglik: formData.manglik,
          horoscope_match_required: formData.horoscope_match_required
        },
        education_career: {
          highest_education: formData.highest_education,
          education_detail: formData.education_detail || null,
          profession: formData.profession,
          profession_detail: formData.profession_detail || null,
          annual_income: formData.annual_income,
          working_city: formData.working_city || null,
          working_country: formData.working_country
        },
        family: {
          father_name: formData.father_name || null,
          father_occupation: formData.father_occupation || null,
          mother_name: formData.mother_name || null,
          mother_occupation: formData.mother_occupation || null,
          family_type: formData.family_type || null,
          family_values: formData.family_values || null,
          family_status: formData.family_status || null,
          brothers: parseInt(formData.brothers) || 0,
          brothers_married: parseInt(formData.brothers_married) || 0,
          sisters: parseInt(formData.sisters) || 0,
          sisters_married: parseInt(formData.sisters_married) || 0,
          family_location: formData.family_location || null,
          native_place: formData.native_place || null,
          about_family: formData.about_family || null
        },
        contact: {
          city: formData.city,
          state: formData.state,
          country: formData.country,
          citizenship: formData.citizenship,
          residency_status: formData.residency_status
        },
        lifestyle: {
          diet: formData.diet,
          smoking: formData.smoking,
          drinking: formData.drinking,
          hobbies: formData.hobbies
        },
        partner_preferences: {
          age_min: parseInt(formData.partner_age_min),
          age_max: parseInt(formData.partner_age_max),
          height_min_cm: parseInt(formData.partner_height_min_cm),
          height_max_cm: parseInt(formData.partner_height_max_cm),
          marital_status: formData.partner_marital_status,
          religion: formData.partner_religion,
          caste: formData.partner_caste,
          education: formData.partner_education,
          profession: formData.partner_profession,
          income_min: formData.partner_income_min || null,
          country: formData.partner_country,
          state: formData.partner_state,
          manglik: formData.partner_manglik,
          about_partner: formData.about_partner || null
        },
        about_me: formData.about_me || null,
        photos: formData.photos,
        profile_photo: formData.profile_photo || null
      };

      await axios.post(`${API}/profile?token=${token}`, payload);
      toast.success('Profile created successfully!');
      navigate('/vivah');
    } catch (error) {
      console.error('Profile creation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create profile');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderBasicInfo();
      case 2:
        return renderReligiousInfo();
      case 3:
        return renderEducationCareer();
      case 4:
        return renderFamilyInfo();
      case 5:
        return renderContactInfo();
      case 6:
        return renderLifestyle();
      case 7:
        return renderPartnerPreferences();
      case 8:
        return renderPhotos();
      default:
        return null;
    }
  };

  const renderBasicInfo = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-gray-800">Basic Information</h2>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Profile Created For *</label>
        <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
          {['Self', 'Son', 'Daughter', 'Brother', 'Sister', 'Relative', 'Friend'].map(opt => (
            <label key={opt} className={`flex items-center justify-center p-3 rounded-xl border-2 cursor-pointer transition-all ${formData.profile_for === opt ? 'border-rose-500 bg-rose-50' : 'border-gray-200 hover:border-gray-300'}`}>
              <input
                type="radio"
                name="profile_for"
                value={opt}
                checked={formData.profile_for === opt}
                onChange={handleInputChange}
                className="hidden"
              />
              <span className="text-sm">{opt}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
          <input
            type="text"
            name="full_name"
            value={formData.full_name}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
            placeholder="Enter full name"
            data-testid="vivah-name"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Gender *</label>
          <div className="flex gap-4">
            {['Male', 'Female'].map(g => (
              <label key={g} className={`flex-1 flex items-center justify-center p-3 rounded-xl border-2 cursor-pointer transition-all ${formData.gender === g ? 'border-rose-500 bg-rose-50' : 'border-gray-200 hover:border-gray-300'}`}>
                <input
                  type="radio"
                  name="gender"
                  value={g}
                  checked={formData.gender === g}
                  onChange={handleInputChange}
                  className="hidden"
                  data-testid={`vivah-gender-${g.toLowerCase()}`}
                />
                <span>{g}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth *</label>
          <input
            type="date"
            name="dob"
            value={formData.dob}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
            data-testid="vivah-dob"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Height (cm) *</label>
          <input
            type="number"
            name="height_cm"
            value={formData.height_cm}
            onChange={handleInputChange}
            min="100"
            max="250"
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
            data-testid="vivah-height"
          />
          <p className="text-xs text-gray-500 mt-1">
            {formData.height_cm ? `≈ ${Math.floor(formData.height_cm / 30.48)}'${Math.round((formData.height_cm % 30.48) / 2.54)}"` : ''}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Weight (kg)</label>
          <input
            type="number"
            name="weight_kg"
            value={formData.weight_kg}
            onChange={handleInputChange}
            min="30"
            max="200"
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Complexion</label>
          <select
            name="complexion"
            value={formData.complexion}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
          >
            <option value="">Select</option>
            {constants?.complexions?.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Body Type</label>
          <select
            name="body_type"
            value={formData.body_type}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
          >
            <option value="">Select</option>
            {constants?.body_types?.map(b => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Marital Status *</label>
        <select
          name="marital_status"
          value={formData.marital_status}
          onChange={handleInputChange}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
        >
          {constants?.marital_status?.map(m => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
      </div>

      {formData.marital_status !== 'Never Married' && (
        <div className="bg-amber-50 p-4 rounded-xl">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="has_children"
              checked={formData.has_children}
              onChange={handleInputChange}
              className="w-4 h-4 text-rose-600 rounded"
            />
            <span className="text-sm text-gray-700">I have children</span>
          </label>
          {formData.has_children && (
            <div className="mt-3">
              <label className="block text-sm text-gray-700 mb-1">Number of children</label>
              <input
                type="number"
                name="number_of_children"
                value={formData.number_of_children}
                onChange={handleInputChange}
                min="0"
                max="10"
                className="w-32 px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderReligiousInfo = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-gray-800">Religious & Astrological Information</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Religion *</label>
          <select
            name="religion"
            value={formData.religion}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
          >
            {constants?.religions?.map(r => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Caste</label>
          <select
            name="caste"
            value={formData.caste}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
          >
            <option value="">Select (Optional)</option>
            {constants?.castes?.[formData.religion]?.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Sub-Caste</label>
          <input
            type="text"
            name="sub_caste"
            value={formData.sub_caste}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
            placeholder="Enter sub-caste (optional)"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Gotra</label>
          <select
            name="gotra"
            value={formData.gotra}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
          >
            <option value="">Select (Optional)</option>
            {constants?.gotras?.map(g => (
              <option key={g} value={g}>{g}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Rashi (Moon Sign)</label>
          <select
            name="rashi"
            value={formData.rashi}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
          >
            <option value="">Select</option>
            {constants?.rashis?.map(r => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nakshatra</label>
          <select
            name="nakshatra"
            value={formData.nakshatra}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
          >
            <option value="">Select</option>
            {constants?.nakshatras?.map(n => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Manglik Status</label>
        <div className="flex gap-4">
          {['Yes', 'No', "Don't Know"].map(opt => (
            <label key={opt} className={`flex-1 flex items-center justify-center p-3 rounded-xl border-2 cursor-pointer transition-all ${formData.manglik === opt ? 'border-rose-500 bg-rose-50' : 'border-gray-200 hover:border-gray-300'}`}>
              <input
                type="radio"
                name="manglik"
                value={opt}
                checked={formData.manglik === opt}
                onChange={handleInputChange}
                className="hidden"
              />
              <span className="text-sm">{opt}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="bg-amber-50 p-4 rounded-xl">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            name="horoscope_match_required"
            checked={formData.horoscope_match_required}
            onChange={handleInputChange}
            className="w-4 h-4 text-rose-600 rounded"
          />
          <span className="text-sm text-gray-700">Horoscope matching is important for me</span>
        </label>
        <p className="text-xs text-gray-500 mt-2">
          If enabled, you can generate your Kundali in the Jyotish section and link it to your profile.
        </p>
      </div>
    </div>
  );

  const renderEducationCareer = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-gray-800">Education & Career</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Highest Education *</label>
          <select
            name="highest_education"
            value={formData.highest_education}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
            data-testid="vivah-education"
          >
            <option value="">Select</option>
            {constants?.education_levels?.map(e => (
              <option key={e} value={e}>{e}</option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">College/University</label>
          <input
            type="text"
            name="education_detail"
            value={formData.education_detail}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
            placeholder="e.g., IIT Delhi, Delhi University"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Profession *</label>
          <select
            name="profession"
            value={formData.profession}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
            data-testid="vivah-profession"
          >
            <option value="">Select</option>
            {constants?.professions?.map(p => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Company/Organization</label>
          <input
            type="text"
            name="profession_detail"
            value={formData.profession_detail}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
            placeholder="e.g., Google, TCS, Government"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Annual Income</label>
          <select
            name="annual_income"
            value={formData.annual_income}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
          >
            {constants?.income_ranges?.map(i => (
              <option key={i} value={i}>{i}</option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Working City</label>
          <input
            type="text"
            name="working_city"
            value={formData.working_city}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
            placeholder="e.g., Mumbai, Bangalore"
          />
        </div>
      </div>
    </div>
  );

  const renderFamilyInfo = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-gray-800">Family Details</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Father's Name</label>
          <input
            type="text"
            name="father_name"
            value={formData.father_name}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Father's Occupation</label>
          <input
            type="text"
            name="father_occupation"
            value={formData.father_occupation}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
            placeholder="e.g., Business, Government Service, Retired"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Mother's Name</label>
          <input
            type="text"
            name="mother_name"
            value={formData.mother_name}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Mother's Occupation</label>
          <input
            type="text"
            name="mother_occupation"
            value={formData.mother_occupation}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
            placeholder="e.g., Homemaker, Teacher"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Family Type</label>
          <select
            name="family_type"
            value={formData.family_type}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
          >
            <option value="">Select</option>
            {constants?.family_types?.map(f => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Family Values</label>
          <select
            name="family_values"
            value={formData.family_values}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
          >
            <option value="">Select</option>
            {constants?.family_values?.map(f => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Family Status</label>
          <select
            name="family_status"
            value={formData.family_status}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
          >
            <option value="">Select</option>
            {constants?.family_status?.map(f => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Brothers</label>
          <input
            type="number"
            name="brothers"
            value={formData.brothers}
            onChange={handleInputChange}
            min="0"
            max="10"
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Married</label>
          <input
            type="number"
            name="brothers_married"
            value={formData.brothers_married}
            onChange={handleInputChange}
            min="0"
            max={formData.brothers}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Sisters</label>
          <input
            type="number"
            name="sisters"
            value={formData.sisters}
            onChange={handleInputChange}
            min="0"
            max="10"
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Married</label>
          <input
            type="number"
            name="sisters_married"
            value={formData.sisters_married}
            onChange={handleInputChange}
            min="0"
            max={formData.sisters}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Family Location</label>
          <input
            type="text"
            name="family_location"
            value={formData.family_location}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
            placeholder="City where family lives"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Native Place</label>
          <input
            type="text"
            name="native_place"
            value={formData.native_place}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
            placeholder="Ancestral village/town"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">About Family</label>
        <textarea
          name="about_family"
          value={formData.about_family}
          onChange={handleInputChange}
          rows={3}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
          placeholder="Write about your family background, values, and traditions..."
        />
      </div>
    </div>
  );

  const renderContactInfo = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-gray-800">Location Details</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
          <input
            type="text"
            name="city"
            value={formData.city}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
            placeholder="Enter your city"
            data-testid="vivah-city"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
          <select
            name="state"
            value={formData.state}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
            data-testid="vivah-state"
          >
            <option value="">Select State</option>
            {constants?.states?.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
          <input
            type="text"
            name="country"
            value={formData.country}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Citizenship</label>
          <input
            type="text"
            name="citizenship"
            value={formData.citizenship}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Residency Status</label>
        <select
          name="residency_status"
          value={formData.residency_status}
          onChange={handleInputChange}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
        >
          <option value="Citizen">Citizen</option>
          <option value="Permanent Resident">Permanent Resident</option>
          <option value="Work Permit">Work Permit</option>
          <option value="Student Visa">Student Visa</option>
          <option value="Temporary Visa">Temporary Visa</option>
        </select>
      </div>
    </div>
  );

  const renderLifestyle = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-gray-800">Lifestyle</h2>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Diet</label>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {['Vegetarian', 'Non-Vegetarian', 'Eggetarian', 'Vegan', 'Jain'].map(opt => (
            <label key={opt} className={`flex items-center justify-center p-3 rounded-xl border-2 cursor-pointer transition-all ${formData.diet === opt ? 'border-rose-500 bg-rose-50' : 'border-gray-200 hover:border-gray-300'}`}>
              <input
                type="radio"
                name="diet"
                value={opt}
                checked={formData.diet === opt}
                onChange={handleInputChange}
                className="hidden"
              />
              <span className="text-sm">{opt}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Smoking</label>
          <div className="flex gap-2">
            {['No', 'Occasionally', 'Yes'].map(opt => (
              <label key={opt} className={`flex-1 flex items-center justify-center p-3 rounded-xl border-2 cursor-pointer transition-all ${formData.smoking === opt ? 'border-rose-500 bg-rose-50' : 'border-gray-200 hover:border-gray-300'}`}>
                <input
                  type="radio"
                  name="smoking"
                  value={opt}
                  checked={formData.smoking === opt}
                  onChange={handleInputChange}
                  className="hidden"
                />
                <span className="text-sm">{opt}</span>
              </label>
            ))}
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Drinking</label>
          <div className="flex gap-2">
            {['No', 'Occasionally', 'Yes'].map(opt => (
              <label key={opt} className={`flex-1 flex items-center justify-center p-3 rounded-xl border-2 cursor-pointer transition-all ${formData.drinking === opt ? 'border-rose-500 bg-rose-50' : 'border-gray-200 hover:border-gray-300'}`}>
                <input
                  type="radio"
                  name="drinking"
                  value={opt}
                  checked={formData.drinking === opt}
                  onChange={handleInputChange}
                  className="hidden"
                />
                <span className="text-sm">{opt}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">About Me</label>
        <textarea
          name="about_me"
          value={formData.about_me}
          onChange={handleInputChange}
          rows={4}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
          placeholder="Write about yourself - your personality, interests, values, and what you're looking for in a life partner..."
        />
        <p className="text-xs text-gray-500 mt-1">This will be shown on your profile. Be genuine and respectful.</p>
      </div>
    </div>
  );

  const renderPartnerPreferences = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-gray-800">Partner Preferences</h2>
      <p className="text-sm text-gray-600">Tell us what you're looking for in a life partner</p>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Age Range</label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              name="partner_age_min"
              value={formData.partner_age_min}
              onChange={handleInputChange}
              min="18"
              max="70"
              className="w-24 px-3 py-2 border border-gray-300 rounded-lg"
            />
            <span className="text-gray-500">to</span>
            <input
              type="number"
              name="partner_age_max"
              value={formData.partner_age_max}
              onChange={handleInputChange}
              min="18"
              max="70"
              className="w-24 px-3 py-2 border border-gray-300 rounded-lg"
            />
            <span className="text-gray-500">years</span>
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Height Range (cm)</label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              name="partner_height_min_cm"
              value={formData.partner_height_min_cm}
              onChange={handleInputChange}
              min="100"
              max="250"
              className="w-24 px-3 py-2 border border-gray-300 rounded-lg"
            />
            <span className="text-gray-500">to</span>
            <input
              type="number"
              name="partner_height_max_cm"
              value={formData.partner_height_max_cm}
              onChange={handleInputChange}
              min="100"
              max="250"
              className="w-24 px-3 py-2 border border-gray-300 rounded-lg"
            />
            <span className="text-gray-500">cm</span>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Marital Status Preference</label>
        <div className="flex flex-wrap gap-2">
          {constants?.marital_status?.map(m => (
            <label key={m} className={`px-4 py-2 rounded-full border-2 cursor-pointer transition-all ${formData.partner_marital_status.includes(m) ? 'border-rose-500 bg-rose-50 text-rose-700' : 'border-gray-200 hover:border-gray-300'}`}>
              <input
                type="checkbox"
                checked={formData.partner_marital_status.includes(m)}
                onChange={() => handleArrayChange('partner_marital_status', m)}
                className="hidden"
              />
              <span className="text-sm">{m}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Religion Preference</label>
        <div className="flex flex-wrap gap-2">
          {constants?.religions?.map(r => (
            <label key={r} className={`px-4 py-2 rounded-full border-2 cursor-pointer transition-all ${formData.partner_religion.includes(r) ? 'border-rose-500 bg-rose-50 text-rose-700' : 'border-gray-200 hover:border-gray-300'}`}>
              <input
                type="checkbox"
                checked={formData.partner_religion.includes(r)}
                onChange={() => handleArrayChange('partner_religion', r)}
                className="hidden"
              />
              <span className="text-sm">{r}</span>
            </label>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-1">Leave empty for any religion</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Manglik Preference</label>
        <div className="flex gap-2">
          {['Yes', 'No', "Doesn't Matter"].map(opt => (
            <label key={opt} className={`flex-1 flex items-center justify-center p-3 rounded-xl border-2 cursor-pointer transition-all ${formData.partner_manglik === opt ? 'border-rose-500 bg-rose-50' : 'border-gray-200 hover:border-gray-300'}`}>
              <input
                type="radio"
                name="partner_manglik"
                value={opt}
                checked={formData.partner_manglik === opt}
                onChange={handleInputChange}
                className="hidden"
              />
              <span className="text-sm">{opt}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">What you're looking for</label>
        <textarea
          name="about_partner"
          value={formData.about_partner}
          onChange={handleInputChange}
          rows={3}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent"
          placeholder="Describe your ideal life partner - qualities, values, expectations..."
        />
      </div>
    </div>
  );

  const renderPhotos = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-gray-800">Photos</h2>
      <p className="text-sm text-gray-600">Add photos to get more responses. Profiles with photos get 10x more interest.</p>
      
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">Photo Guidelines</p>
            <ul className="text-xs text-amber-700 mt-1 space-y-1">
              <li>• Upload clear, recent photos</li>
              <li>• Face should be clearly visible</li>
              <li>• Avoid group photos as main profile photo</li>
              <li>• No inappropriate or edited photos</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center">
        <Camera className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600 mb-2">Photo upload will be available after profile creation</p>
        <p className="text-sm text-gray-500">You can add photos from your profile settings later</p>
      </div>

      <div className="bg-green-50 border border-green-200 rounded-xl p-4">
        <div className="flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <div>
            <p className="text-sm font-medium text-green-800">You're almost done!</p>
            <p className="text-xs text-green-700">Click "Create Profile" to complete your registration</p>
          </div>
        </div>
      </div>
    </div>
  );

  if (!constants) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-amber-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-rose-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-amber-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-rose-600 to-amber-600 text-white py-4 px-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button onClick={() => navigate('/vivah')} className="p-2 hover:bg-white/10 rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold">Create Your Profile</h1>
            <p className="text-rose-200 text-sm">DharmVivah - धर्मविवाह</p>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex justify-between mb-8 overflow-x-auto pb-2">
          {STEPS.map((step) => (
            <div
              key={step.id}
              className={`flex flex-col items-center min-w-[60px] ${
                step.id === currentStep
                  ? 'text-rose-600'
                  : step.id < currentStep
                  ? 'text-green-600'
                  : 'text-gray-400'
              }`}
            >
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 ${
                  step.id === currentStep
                    ? 'bg-rose-100 border-2 border-rose-600'
                    : step.id < currentStep
                    ? 'bg-green-100'
                    : 'bg-gray-100'
                }`}
              >
                {step.id < currentStep ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  <step.icon className="w-5 h-5" />
                )}
              </div>
              <span className="text-xs text-center hidden md:block">{step.title}</span>
            </div>
          ))}
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          {renderStepContent()}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between">
          <button
            onClick={prevStep}
            disabled={currentStep === 1}
            className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Previous
          </button>
          
          {currentStep < STEPS.length ? (
            <button
              onClick={nextStep}
              className="px-6 py-3 bg-gradient-to-r from-rose-600 to-amber-600 text-white font-medium rounded-xl hover:shadow-lg flex items-center gap-2"
            >
              Next
              <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="px-8 py-3 bg-gradient-to-r from-rose-600 to-amber-600 text-white font-semibold rounded-xl hover:shadow-lg disabled:opacity-50 flex items-center gap-2"
              data-testid="create-profile-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  Create Profile
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
